import { useNavigation } from '@react-navigation/core'
import { useDispatch } from 'react-redux'
import React, { useEffect } from 'react'
import { Product, Subscription, SubscriptionStatus } from '@src/types.ts'
import { Actions } from '@screens/Marketplace/components/ProgramDetails/Actions.tsx'
import { useUpsellOffer } from '@src/hooks/useUpsellOffer'
import { useLazyScreenPreload } from '@src/navigation/utils'

export const ProgramDetailsActions = ({
  subscription,
}: {
  subscription: Pick<Subscription, 'status' | 'cancelAt' | 'paused' | 'id'> & {
    primaryProduct: Pick<Product, 'uncancelable'>
  }
}) => {
  const navigation = useNavigation()
  const dispatch = useDispatch()
  const paymentPastDue = subscription.status === SubscriptionStatus.PastDue

  const upsellOffer = useUpsellOffer()

  const isUpsellOfferForCurrentSubscription = upsellOffer?.subscriptionId === subscription.id

  useEffect(() => {
    dispatch({
      type: 'app/fetchProducts',
    })
  }, [dispatch])

  useLazyScreenPreload('CancelSubscriptionOtherOptions')
  useLazyScreenPreload('ReactivateSubscriptionModal', {
    shouldPreload: subscription.cancelAt && subscription.primaryProduct.uncancelable,
  })
  useLazyScreenPreload('ResumeSubscriptionModal', { shouldPreload: subscription.paused })
  useLazyScreenPreload('SupportHub', { shouldPreload: paymentPastDue })

  const getPrimaryButton = () => {
    if (isUpsellOfferForCurrentSubscription) {
      return {
        text: upsellOffer.upgradeCallToAction,
        onPress: () => navigation.navigate('ProductExplanation', { upsellOffer }),
      }
    }

    if (subscription.cancelAt && subscription.primaryProduct.uncancelable) {
      return {
        text: 'Reactivate Subscription',
        onPress: () =>
          navigation.navigate('ReactivateSubscriptionModal', { subscriptionId: subscription.id }),
      }
    }

    return undefined
  }

  const getSecondaryButton = () => {
    if (paymentPastDue) {
      return {
        text: 'Contact Support',
        onPress: () => navigation.navigate('SupportHub', {}),
      }
    }

    if (subscription.cancelAt) {
      if (subscription.primaryProduct.uncancelable && isUpsellOfferForCurrentSubscription) {
        return {
          text: 'Reactivate Subscription',
          onPress: () =>
            navigation.navigate('ReactivateSubscriptionModal', { subscriptionId: subscription.id }),
        }
      } else {
        return undefined
      }
    }

    if (subscription.paused) {
      return {
        text: 'Resume Subscription',
        onPress: () => navigation.navigate('ResumeSubscriptionModal'),
      }
    }

    return {
      text: isUpsellOfferForCurrentSubscription ? 'Cancel Subscription' : 'Modify Subscription',
      onPress: () =>
        navigation.navigate('CancelSubscriptionOtherOptions', {
          subscriptionId: subscription.id,
        }),
    }
  }

  const primaryButton = getPrimaryButton()
  const secondaryButton = getSecondaryButton()

  return (
    <Actions
      primaryButtonText={primaryButton?.text}
      onPrimaryButtonPress={primaryButton?.onPress}
      secondaryButtonText={secondaryButton?.text}
      secondaryButtonType={subscription.paused ? 'outline' : 'transparent'}
      onSecondaryButtonPress={secondaryButton?.onPress}
    />
  )
}
